// Variante elegida por modelo (`group.key`) en «Elige el teléfono», recordada en la pestaña por
// sesión del wizard. La línea de la sesión y el `?sku=` ganan sobre lo recordado.
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ModelGroup } from '@/domains/sales/portal/catalog/lib/modelGroups';
import type { CatalogItem } from './types';

/** `group.key` → SKU elegido. */
export type VariantMemory = Readonly<Record<string, string>>;

const keyOf = (sessionId: string): string => `wizard:variant-choice:${sessionId}`;

export function loadVariantMemory(sessionId: string): VariantMemory {
    try {
        const raw = sessionStorage.getItem(keyOf(sessionId));
        if (raw === null) return {};
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
        return Object.fromEntries(
            Object.entries(parsed).filter(
                (entry): entry is [string, string] => typeof entry[1] === 'string',
            ),
        );
    } catch {
        return {};
    }
}

export function saveVariantMemory(sessionId: string, memory: VariantMemory): void {
    try {
        sessionStorage.setItem(keyOf(sessionId), JSON.stringify(memory));
    } catch {
        /* sin almacenamiento cada modelo vuelve a abrir sin variante elegida */
    }
}

/** Quita los modelos cuyo SKU recordado ya no está entre sus variantes. */
export function pruneVariantMemory(
    memory: VariantMemory,
    groups: readonly ModelGroup[],
): VariantMemory {
    const byKey = new Map(groups.map((group) => [group.key, group]));
    const kept = Object.entries(memory).filter(([key, sku]) => {
        const group = byKey.get(key);
        return group === undefined || group.variants.some((item) => item.sku === sku);
    });
    return kept.length === Object.keys(memory).length ? memory : Object.fromEntries(kept);
}

/** SKU que mandan sobre lo recordado: el de la línea de la sesión y luego el `?sku=`. */
export interface VariantPriority {
    sessionSku?: string | null;
    urlSku?: string | null;
}

/** La variante elegida del modelo, o `null` si nadie eligió y toca mostrar «desde». */
export function chosenVariantOf<T extends CatalogItem>(
    group: ModelGroup<T>,
    memory: VariantMemory,
    priority: VariantPriority = {},
): T | null {
    if (group.variants.length === 1) return group.variants[0];
    for (const sku of [priority.sessionSku, priority.urlSku, memory[group.key]]) {
        if (!sku) continue;
        const found = group.variants.find((item) => item.sku === sku);
        if (found) return found;
    }
    return null;
}

/** Memoria de la pestaña con su escritura; descarta SKU que ya no existen al cambiar los grupos. */
export function useVariantMemory(sessionId: string, groups: readonly ModelGroup[]) {
    const [stored, setStored] = useState<VariantMemory>(() => loadVariantMemory(sessionId));
    const memory = useMemo(() => pruneVariantMemory(stored, groups), [stored, groups]);

    useEffect(() => {
        if (memory !== stored) saveVariantMemory(sessionId, memory);
    }, [sessionId, memory, stored]);

    const remember = useCallback(
        (groupKey: string, sku: string) => {
            const next = { ...memory, [groupKey]: sku };
            saveVariantMemory(sessionId, next);
            setStored(next);
        },
        [sessionId, memory],
    );

    return { memory, remember };
}
